import React from 'react';

import { computeNewItemPlacements, computeReorderMovers } from './reorderDiff';

/** How many lines (moved items plus placed new items) the "what moved" summary will name before collapsing
 * to a compact count instead — keeps the row from turning into a wall of text when most of a large
 * collection shuffled. */
const MANY_MOVERS_THRESHOLD = 5;

interface ReorderMoversSummaryProps {
  before: string[];
  after: string[];
  keyLabels: Record<string, string>;
  /** The order the change names (`after_value`): the promoted order on approve, the restore order on
   * rollback. When the change has a resolved order, `after` is that order, which only rearranges the
   * target's current members, so a key the target does not have yet appears only here. When given, each new
   * key it places ahead of existing items gets a line. The conflict card leaves it out, because its `before`
   * is the live order, and a key missing from that may have been deleted on the target rather than be new. */
  promotedOrder?: string[];
}

/** Named-mover "what moved" lines for one pair of orderings (`Reminder: #1 → #3`) and the position of each
 * new item the reorder places (`Service Type: new → #1`), falling back to a compact count when there is
 * nothing to name (a membership-only difference riding along with the reorder) or when so much moved that
 * one line per item stops being legible. */
const ReorderMoversSummary: React.FC<ReorderMoversSummaryProps> = ({
  before,
  after,
  keyLabels,
  promotedOrder = [],
}) => {
  // Drop any "mover" whose two printed positions are the same. The LCS picks one stable subsequence, and
  // when several are equally long its tie-break can anchor on a different item than the reader would,
  // leaving something in the complement whose absolute index never changed — printed as "X: #2 → #2", a
  // move to the position it already occupies, contradicting the order list where the same item renders
  // plain. Suppressed before the count, or the "N items reordered" collapse would tally movers it then
  // refuses to name.
  const movers = computeReorderMovers(before, after, keyLabels).filter((mover) => mover.fromIndex !== mover.toIndex);
  const placements = computeNewItemPlacements(before, promotedOrder, keyLabels);
  const lineCount = movers.length + placements.length;
  const useCountSummary = lineCount === 0 || lineCount > MANY_MOVERS_THRESHOLD;
  const summaryCount = lineCount > 0 ? lineCount : Math.max(before.length, after.length);
  if (useCountSummary) {
    return <div>{`${summaryCount} items reordered`}</div>;
  }
  const lines = (
    <div className="grid w-fit grid-cols-[auto_1fr] gap-x-16 gap-y-4 text-sm">
      {movers.map((mover) => (
        <React.Fragment key={mover.key}>
          <span className="font-bold">{`${mover.label}:`}</span>
          <div className="rounded bg-slate-200 px-4 py-2">{` #${mover.fromIndex + 1} → #${mover.toIndex + 1}`}</div>
        </React.Fragment>
      ))}
      {placements.map((placement) => (
        <React.Fragment key={placement.key}>
          <span className="font-bold">{`${placement.label}:`}</span>
          <div className="rounded bg-green-100 px-4 py-2">{` new → #${placement.toIndex + 1}`}</div>
        </React.Fragment>
      ))}
    </div>
  );
  if (movers.length > 0) {
    return lines;
  }
  return (
    <div className="flex flex-col gap-y-4">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-800">Position of new items</span>
      {lines}
    </div>
  );
};

export default ReorderMoversSummary;
